import { createClient } from '@supabase/supabase-js';
import config from '../config';

const supabase = createClient(
  config.supabase.url,
  config.supabase.key
);

const buildUrl = (endpoint) => `${config.api.baseUrl}${endpoint}`;

const getAuthHeaders = async () => {
  const accessToken = localStorage.getItem('access_token');
  return {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  };
};

export const chatService = {
    async sendMessage(message, conversationId = null) {
        try {
            const response = await fetch(buildUrl(config.endpoints.chat), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ 
                    message,
                    conversation_id: conversationId 
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (response.status === 202) {
                return this.pollForResponse(data.conversation_id);
            }

            if (!data || !data.final_answer) {
                throw new Error('Invalid response format from server');
            }

            const processedResponse = {
                response: data.final_answer,
                sources: data.sources || [],
                reasoning: data.reasoning || null,
                conversationId: data.conversation_id,
                message_id: data.message_id,
                debug: data.debug || null
            };
            return processedResponse;
        } catch (error) {
            throw error;
        }
    },

    async pollForResponse(conversationId, maxAttempts = 20) {
        const pollInterval = 3000; // 3 seconds
        
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
                const response = await fetch(buildUrl(`${config.endpoints.conversations}/${conversationId}/latest`), {
                    credentials: 'include'
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                
                if (data.status === 'completed') {
                    return {
                        response: data.final_answer,
                        sources: data.sources || [],
                        reasoning: data.reasoning || null,
                        conversationId: conversationId,
                        message_id: data.message_id,
                        debug: data.debug || null
                    };
                }
                
                // If still processing, wait before next attempt
                await new Promise(resolve => setTimeout(resolve, pollInterval));
                
            } catch (error) {
                throw error;
            }
        }
        
        throw new Error('Request timed out after maximum polling attempts');
    },

    async getConversations() {
        try {
            const response = await fetch(buildUrl(config.endpoints.conversations), {
                method: 'GET',
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Failed to fetch conversations');
            const data = await response.json();
            return data.conversations || [];
        } catch (error) {
            return [];
        }
    },

    async getConversationHistory(conversationId) {
        try {
            const response = await fetch(buildUrl(`${config.endpoints.conversations}/${conversationId}/messages`), {
                method: 'GET',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const messages = await response.json();
            return messages.map(msg => {
                // For assistant messages, try to parse the content if it's a JSON string
                if (msg.role === 'assistant') {
                    try {
                        const parsedContent = JSON.parse(msg.content);
                        return {
                            role: msg.role,
                            content: parsedContent.final_answer,
                            sources: Array.isArray(parsedContent.sources) 
                                ? parsedContent.sources 
                                : (typeof parsedContent.sources === 'string' 
                                    ? parsedContent.sources.split('\n')
                                        .filter(line => line.trim())
                                        .map(line => line.trim())
                                        .filter(line => line.startsWith('-'))
                                        .map(line => line.substring(1).trim()) 
                                    : []),
                            reasoning: parsedContent.reasoning || null,
                            message_id: msg.message_id,
                            debug: msg.debug || null
                        };
                    } catch (e) {
                        // If not JSON, treat it as plain text
                        return {
                            role: msg.role,
                            content: msg.content,
                            sources: [],
                            reasoning: null,
                            message_id: msg.message_id,
                            debug: msg.debug || null
                        };
                    }
                }
                // For user messages, return as is
                return {
                    role: msg.role,
                    content: msg.content,
                    message_id: msg.message_id,
                    debug: msg.debug || null
                };
            });
        } catch (error) {
            throw error;
        }
    },

    async deleteConversation(conversationId) {
        const response = await fetch(buildUrl(`${config.endpoints.conversations}/${conversationId}/hide`), {
            method: 'POST',
            credentials: 'include'
        });
        if (!response.ok) throw new Error('Failed to delete conversation');
        return response.json();
    },

    async generateTitle(conversationId) {
        const response = await fetch(buildUrl(`${config.endpoints.conversations}/${conversationId}/title`), {
            method: 'POST',
            credentials: 'include'
        });
        if (!response.ok) throw new Error('Failed to generate title');
        return response.json();
    },

    async get(url) {
        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include'
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    },

    async post(url, data) {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(data)
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    },

    async getSystemPrompt() {
        return await this.getSetting('system-prompt');
    },

    async updateSystemPrompt(prompt) {
        return await this.updateSetting('system-prompt', prompt);
    },

    async getSetting(key) {
        try {
            const response = await this.get(buildUrl(`${config.endpoints.settings}/${key}`));
            return response.value;
        } catch (error) {
            if (error.message.includes('not found')) {
                return null;
            }
            throw error;
        }
    },

    async updateSetting(key, value) {
        return this.post(buildUrl(`${config.endpoints.settings}/${key}`), { value });
    },

    async updateVectorStoreSettings(prompt, assistantId, vectorstoreId) {
        await Promise.all([
            this.updateSetting('vector-store-prompt', prompt),
            this.updateSetting('assistant-id', assistantId),
            this.updateSetting('vectorstore-id', vectorstoreId)
        ]);
    },

    async updateProductClassifierSettings(prompt, threshold) {
        await Promise.all([
            this.updateSetting('product-classifier-prompt', prompt),
            this.updateSetting('product-classifier-threshold', threshold)
        ]);
    },

    async getVectorClassifierSetting(key) {
        return this.getSetting(`vector-classifier-${key}`);
    },

    async updateVectorClassifierSetting(key, value) {
        return this.updateSetting(`vector-classifier-${key}`, value);
    },

    async getLeads() {
        try {
            const response = await fetch(buildUrl(config.endpoints.leads), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                credentials: 'include'
            });
            if (!response.ok) {
                throw new Error('Failed to fetch leads');
            }
            const result = await response.json();
            return result;  
        } catch (error) {
            return { data: [], error: error.message };
        }
    },

    async userInfo() {
        try {
            const accessToken = localStorage.getItem('access_token');
            const headers = {
                'Content-Type': 'application/json'
            };
            
            if (accessToken) {
                headers['Authorization'] = `Bearer ${accessToken}`;
            }
            
            const response = await fetch(buildUrl(config.endpoints.userInfo), {
                method: 'GET',
                headers,
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch user data: ${response.status}`);
            }
            return response.json();
        } catch (error) {
            throw error;
        }
    },

    async deleteLead(leadId) {
        const response = await fetch(`${config.api.baseUrl}${config.endpoints.leads}/${leadId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include'
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to delete lead');
        }
    },

    async sendFeedback(messageId, conversationId, isPositive, feedbackType = null, feedbackText = null) {
        try {
            const response = await fetch(buildUrl('/api/feedback'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    message_id: messageId,
                    conversation_id: conversationId,
                    is_positive: isPositive,
                    feedback_type: feedbackType,
                    feedback_text: feedbackText
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    async getFeedbackStatus(messageIds) {
        try {
            const response = await fetch(buildUrl(`/api/feedback/status?message_ids=${messageIds}`), {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    async getFeedbackWithContext() {
        try {
            const response = await fetch(buildUrl('/api/feedback/with-context'), {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    async getFeedbackStats() {
        try {
            const response = await fetch(buildUrl('/api/feedback/stats'), {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    async getMonthlyConversationStats() {
        try {
            const response = await fetch(buildUrl('/api/conversation/monthly-stats'), {
                method: 'GET',
                credentials: 'include'
            });
            if (!response.ok) throw new Error('Failed to fetch monthly conversation stats');
            return await response.json();
        } catch (error) {
            return null;
        }
    },

    async getAllConversations() {
        try {
            const response = await fetch(buildUrl('/api/conversations/all'), {
                method: 'GET',
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    async uploadFile(formData) {
        try {
            const headers = await getAuthHeaders();
            delete headers['Content-Type'];

            const response = await fetch(buildUrl('/api/upload'), {
                method: 'POST',
                headers: headers,
                body: formData
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Upload failed: ${response.status === 404 ? 'File not found' : errorText}`);
            }
            
            return response.json();
        } catch (error) {
            throw error;
        }
    },

    async listFiles(folderPath = '') {
        const params = new URLSearchParams();
        if (folderPath) {
            params.append('folder', folderPath);
        }
        
        const response = await fetch(buildUrl(`/api/files?${params}`), {
            headers: await getAuthHeaders()
        });
        
        if (!response.ok) {
            throw new Error(`Failed to list files: ${response.statusText}`);
        }
        
        return response.json();
    },

    async deleteFile(fileId) {
        const response = await fetch(buildUrl(`/api/files/${fileId}`), {
            method: 'DELETE',
            headers: await getAuthHeaders()
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to delete file: ${response.status === 404 ? 'File not found' : errorText}`);
        }
        
        return response.json();
    },

    async createFolder(data) {
        const response = await fetch(buildUrl('/api/folders'), {
            method: 'POST',
            headers: {
                ...(await getAuthHeaders()),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        
        if (!response.ok) {
            throw new Error(`Failed to create folder: ${response.statusText}`);
        }
        
        return response.json();
    },

    async moveFile(fileId, destinationFolder) {
        const response = await fetch(buildUrl(`/api/files/${fileId}/move`), {
            method: 'POST',
            headers: {
                ...(await getAuthHeaders()),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ destination_folder: destinationFolder })
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to move file: ${response.status === 404 ? 'File not found' : errorText}`);
        }
        
        return response.json();
    },

    async deleteFolder(folderPath) {
        const response = await fetch(buildUrl(`/api/folders/${folderPath}`), {
            method: 'DELETE',
            headers: await getAuthHeaders()
        });
        
        if (!response.ok) {
            throw new Error(`Failed to delete folder: ${response.statusText}`);
        }
        
        return response.json();
    }
};